/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { AvatarGroup, Box, Typography } from '@mui/material';

/*  Shared  */
import {
    DarkTooltip,
    LightTooltip
} from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

const iconStyle = {
    alignContent: 'center',
    display: 'flex',
    marginRight: '8px'
};

const PersonalIcon = () => {
    const { t } = useTranslation();
    return (
        <DarkTooltip
            arrow
            placement="bottom"
            title={t('Tooltips.account.personal')}
        >
            <Box>
                <IconWithVariant
                    variant="primary"
                    src="ProfileWithoutCircle"
                    size={FontSizes[2]}
                    wrapperProps={{ ...iconStyle }}
                />
            </Box>
        </DarkTooltip>
    );
};

const ServiceIcon = () => {
    const { t } = useTranslation();
    return (
        <DarkTooltip
            arrow
            placement="bottom"
            title={t('Tooltips.account.service')}
        >
            <Box>
                <IconWithVariant
                    variant="primary"
                    src="BuildWrench"
                    size={FontSizes[2]}
                    wrapperProps={{ ...iconStyle }}
                />
            </Box>
        </DarkTooltip>
    );
};

const SharedIcon = () => {
    const { t } = useTranslation();
    return (
        <DarkTooltip
            arrow
            placement="bottom"
            title={t('Tooltips.account.shared')}
        >
            <Box>
                <IconWithVariant
                    variant="primary"
                    src="Users"
                    size={FontSizes[2]}
                    wrapperProps={{ ...iconStyle }}
                />
            </Box>
        </DarkTooltip>
    );
};

const WorkIcon = () => {
    const { t } = useTranslation();
    return (
        <DarkTooltip
            arrow
            placement="bottom"
            title={t('Tooltips.account.work')}
        >
            <Box>
                <IconWithVariant
                    variant="primary"
                    src="Domain"
                    size={FontSizes[2]}
                    wrapperProps={{ ...iconStyle }}
                />
            </Box>
        </DarkTooltip>
    );
};

const ListByType = ({
    byTypes,
    noIcon,
    type,
    types,
    userAccount,
    usernames
}) => (
    <Box
        display="flex"
        alignItems="center"
        data-testid="listUserAccountsByType"
    >
        {byTypes && (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
            >
                <Box>
                    <Typography
                        variant="span"
                        sx={{
                            '> div': { display: 'inline' },
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {userAccount}
                    </Typography>
                </Box>
                {!noIcon && (
                    <Box className="no-print" display="flex" ml="4px">
                        {types.includes('personal') && <PersonalIcon />}
                        {types.includes('service') && <ServiceIcon />}
                        {types.includes('shared') && <SharedIcon />}
                        {types.includes('work') && <WorkIcon />}
                    </Box>
                )}
            </Box>
        )}

        {!byTypes && (
            <>
                {type === 'personal' && !noIcon && <PersonalIcon />}
                {type === 'service' && !noIcon && <ServiceIcon />}
                {type === 'shared' && !noIcon && <SharedIcon />}
                {type === 'work' && !noIcon && <WorkIcon />}
                <Typography
                    variant="span"
                    sx={{
                        '> div': { display: 'inline' },
                        whiteSpace: 'nowrap'
                    }}
                >
                    {usernames[0]}
                    {usernames.length > 1 && ', ...'}
                </Typography>
                {usernames.length > 1 && (
                    <LightTooltip
                        title={usernames.map((username) => (
                            <Box key={username}>
                                <Box display="flex" alignItems="center">
                                    {type === 'personal' && !noIcon && (
                                        <PersonalIcon />
                                    )}
                                    {type === 'service' && !noIcon && (
                                        <ServiceIcon />
                                    )}
                                    {type === 'shared' && !noIcon && (
                                        <SharedIcon />
                                    )}
                                    {type === 'work' && !noIcon && <WorkIcon />}
                                    {username}
                                </Box>
                            </Box>
                        ))}
                        arrow
                        placement="right"
                    >
                        <AvatarGroup
                            total={usernames.length - 1}
                            data-testid="listUserAccountsMore"
                        />
                    </LightTooltip>
                )}
            </>
        )}
    </Box>
);

/*
The prop multipleAccounts makes the accounts to be displayed by type. As in
   personalAccountIcon paAccount1, ... +n <- tooltip displaying all the personal accounts
   serviceAccountIcon saAccount1,... +n <- tooltip displaying all the service accounts
   sharedAccountIcon shaAccount3,... +n <- tooltip displaying all the shared accounts
   workAccountsIcon waAccount1,... +n <- tooltip displaying all the work accounts

If there are the prop multipleAccounts is not set, then the account and all the icons that represent the account type are displayed, e.g.,
   account1     serviceAccountIcon sharedAccountIcon
   account2     personalAccountIcon
   account3     serviceAccountIcon workAccountsIcon
*/
export const ListUserAccounts = ({ accounts, multipleAccounts, noIcon }) => {
    if (!accounts?.length) {
        return null;
    }

    const accountsMap = accounts.reduce(
        (acc, account) => {
            if (account.personal) {
                acc.personal.push(account.username);
                acc.types.push('personal');
            }
            if (account.service) {
                acc.service.push(account.username);
                acc.types.push('service');
            }
            if (account.shared) {
                acc.shared.push(account.username);
                acc.types.push('shared');
            }
            // For some of this components' usage, if it is not personal, it is implied it is work.
            if (
                account.work ||
                (!account.personal && !account.service && !account.shared)
            ) {
                acc.work.push(account.username);
                acc.types.push('work');
            }
            acc.userAccount.push(account.username);
            return acc;
        },
        {
            personal: [],
            service: [],
            shared: [],
            types: [],
            userAccount: [],
            work: []
        }
    );

    return (
        <Box data-testid="listUserAccounts">
            {!multipleAccounts && (
                <ListByType
                    byTypes
                    noIcon={noIcon}
                    types={accountsMap.types}
                    userAccount={accountsMap.userAccount[0]}
                />
            )}
            {multipleAccounts && (
                <>
                    {accountsMap.personal.length > 0 && (
                        <ListByType
                            noIcon={noIcon}
                            type="personal"
                            usernames={accountsMap.personal}
                        />
                    )}
                    {accountsMap.service.length > 0 && (
                        <ListByType
                            noIcon={noIcon}
                            type="service"
                            usernames={accountsMap.service}
                        />
                    )}
                    {accountsMap.shared.length > 0 && (
                        <ListByType
                            noIcon={noIcon}
                            type="shared"
                            usernames={accountsMap.shared}
                        />
                    )}
                    {accountsMap.work.length > 0 && (
                        <ListByType
                            noIcon={noIcon}
                            type="work"
                            usernames={accountsMap.work}
                        />
                    )}
                </>
            )}
        </Box>
    );
};
